import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from './cn';
import { Icon } from './icons';

const FOCUS =
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]';

export interface ScreenHeaderProps {
  /** The screen's title (the page's one h1). */
  title: ReactNode;
  /** The logical parent: where ‹ goes, and its name for "Back to …". */
  back: { to: string; label: string };
  /** Optional controls on the right (Edit, Add, …). */
  actions?: ReactNode;
  className?: string;
}

/**
 * v0.7.5 (TAHI-22): the header of every screen that isn't a top-level or module main screen.
 * ‹ is a real link to the logical parent, not history.back(), so it works when the screen was
 * opened from a tile, a link or a PWA shortcut (an installed PWA has no browser back button).
 */
export function ScreenHeader({ title, back, actions, className }: ScreenHeaderProps) {
  return (
    // Wraps on a phone: the actions drop under the title instead of squeezing it.
    <header
      className={cn('flex flex-wrap items-center justify-between gap-x-3 gap-y-2', className)}
    >
      <div className="flex min-w-0 flex-[1_1_12rem] items-center gap-1">
        <Link
          to={back.to}
          aria-label={`Back to ${back.label}`}
          title={`Back to ${back.label}`}
          className={cn(
            '-ml-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-fg hover:bg-secondary',
            FOCUS,
          )}
        >
          <Icon name="back" size={24} />
        </Link>
        <h1 className="min-w-0 flex-1 truncate text-[26px] font-bold leading-tight text-fg">
          {title}
        </h1>
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  );
}
